import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Typography, Tag, Button, Space, Image,
} from 'antd';
import { PlusOutlined, EyeOutlined, SendOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/lib/table';
import { SocialPost } from 'services/social.service';
import { useSocialPosts, usePublishSocialPost } from 'hooks/api/useSocialPost';
import Routes from 'config/routes';

const { Title, Text } = Typography;

const PAGE_SIZE = 20;

const platformColor = (status?: string) => {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'error';
  return 'default';
};

const SocialList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const skip = (page - 1) * PAGE_SIZE;

  const { data, isFetching } = useSocialPosts({ skip, limit: PAGE_SIZE });
  const { mutate: publish, isLoading: publishing, variables: publishingId } = usePublishSocialPost();

  const columns: ColumnsType<SocialPost> = [
    {
      title: 'Зображення',
      dataIndex: 'enhancedImage',
      width: 80,
      render: (src: string) => (
        <Image src={src} width={56} height={56} style={{ objectFit: 'cover', borderRadius: 6 }} preview={false} />
      ),
    },
    {
      title: 'Продукт',
      dataIndex: 'productName',
      render: (name: string, record: SocialPost) => (
        <div>
          <Text strong>{name}</Text>
          <Text type='secondary' style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
            {record.caption.slice(0, 60)}
            {record.caption.length > 60 ? '...' : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Платформи',
      width: 160,
      render: (_: unknown, record: SocialPost) => (
        <Space direction='vertical' size={4}>
          {record.instagram.enabled && (
            <Tag color={platformColor(record.instagram.publishStatus)}>Instagram</Tag>
          )}
          {record.tiktok.enabled && (
            <Tag color={platformColor(record.tiktok.publishStatus)}>TikTok</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 120,
      render: (status: string) => (status === 'published'
        ? <Tag color='success'>Опубліковано</Tag>
        : <Tag color='orange'>Чернетка</Tag>),
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('uk-UA'),
    },
    {
      title: '',
      width: 140,
      render: (_: unknown, record: SocialPost) => (
        <Space>
          <Button
            size='small'
            icon={<EyeOutlined />}
            onClick={() => navigate(Routes.SOCIAL_VIEW.replace(':id', record._id))}
          >
            Перегляд
          </Button>
          {record.status === 'created' && (
            <Button
              size='small'
              type='primary'
              icon={<SendOutlined />}
              loading={publishing && publishingId === record._id}
              onClick={() => publish(record._id)}
            >
              Публікувати
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Пости в соцмережах</Title>
        <Button type='primary' icon={<PlusOutlined />} onClick={() => navigate(Routes.SOCIAL_CREATE)}>
          Новий пост
        </Button>
      </Space>

      <Table<SocialPost>
        columns={columns}
        dataSource={data?.data || []}
        rowKey='_id'
        loading={isFetching}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.total || 0,
          onChange: setPage,
          showTotal: (total) => `Всього: ${total}`,
        }}
      />
    </Card>
  );
};

export default SocialList;
